<template>
	<div>
		<div class="status-banner-wrapper">
			<Transition name="slide">
				<div
					v-if="isVisible && isStaff"
					class="status-banner hideOnPrint"
					:class="{
						'status-banner--offline': isOffline,
						'status-banner--success': !isOffline && isSuccess,
						'status-banner--error': !isOffline && isError
					}"
				>
					<div class="status-banner__content">
						<span class="status-banner__text">{{ statusMessage }}</span>
					</div>
				</div>
			</Transition>
		</div>

		<!-- Separate progress bar -->
		<div class="progress-wrapper hideOnPrint">
			<div
				v-if="isStaff"
				class="status-banner__progress"
			>
				<div
					class="status-banner__progress-bar"
					:class="{
						'status-banner__progress-bar--loading': isLoading,
						'status-banner__progress-bar--success': isSuccess,
						'status-banner__progress-bar--error': isError
					}"
				></div>
			</div>
		</div>
	</div>
</template>

<script>
	import { EventBus, Events } from '../../../eventBus'
	import { getStoredContract } from '../../../store/db'

	export default {
		name: 'StatusBanner',
		props: {
			isStaff: {
				type: Boolean,
				default: false
			},
			token: {
				type: String,
				required: true
			}
		},
		data() {
			return {
				isOffline: false,
				status: '', // 'loading', 'success', 'error', or ''
				message: '',
				hideTimeout: null,
				hasUnsavedChanges: false
			}
		},
		computed: {
			/**
			 * Whether the status banner is visible
			 * @returns {boolean} True if the status banner is visible, false otherwise
			 */
			isVisible() {
				return this.isOffline || this.status !== ''
			},
			/**
			 * Whether the status banner is loading
			 * @returns {boolean} True if the status banner is loading, false otherwise
			 */
			isLoading() {
				return this.status === 'loading'
			},
			/**
			 * Whether the status banner is success
			 * @returns {boolean} True if the status banner is success, false otherwise
			 */
			isSuccess() {
				return this.status === 'success'
			},
			/**
			 * Whether the status banner is error
			 * @returns {boolean} True if the status banner is error, false otherwise
			 */
			isError() {
				return this.status === 'error'
			},
			/**
			 * The status message
			 * @returns {string} The status message
			 */
			statusMessage() {
				if (this.isOffline) {
					return this.hasUnsavedChanges
						? 'You are currently offline. There are unsaved changes that will be synced when you reconnect.'
						: 'You are currently offline'
				}
				return this.message
			}
		},
		watch: {
			isOffline(newValue) {
				// When coming back online
				if (!newValue && this.hasUnsavedChanges) {
					this.showLoading('Syncing changes...')
				}
			}
		},
		async mounted() {
			window.addEventListener('online', this.handleOnlineStatus)
			window.addEventListener('offline', this.handleOnlineStatus)

			// Initialize offline status and check stored contract
			this.isOffline = !navigator.onLine
			await this.checkStoredContract()

			// Listen for unsaved changes events
			EventBus.$on(Events.CONTRACT_UNSAVED_CHANGES, this.handleUnsavedChanges)
		},
		beforeDestroy() {
			window.removeEventListener('online', this.handleOnlineStatus)
			window.removeEventListener('offline', this.handleOnlineStatus)
			EventBus.$off(Events.CONTRACT_UNSAVED_CHANGES, this.handleUnsavedChanges)
			this.clearHideTimeout()
		},
		methods: {
			/**
			 * Check if the stored contract has unsaved changes
			 */
			async checkStoredContract() {
				if (this.token) {
					const storedContract = await getStoredContract(this.token)
					if (storedContract?.data?.hasOfflineChanges) {
						this.hasUnsavedChanges = true
						if (this.isOffline) {
							this.showLoading("Changes will be saved when you're back online")
						}
					}
				}
			},
			/**
			 * Handle the online status
			 */
			handleOnlineStatus() {
				const wasOffline = this.isOffline
				this.isOffline = !navigator.onLine

				// If coming back online and there are unsaved changes
				if (wasOffline && !this.isOffline && this.hasUnsavedChanges) {
					EventBus.$emit(Events.SYNC_OFFLINE_CHANGES)
				}
			},
			/**
			 * Handle unsaved changes
			 * @param {boolean} hasChanges - Whether the contract has unsaved changes
			 */
			handleUnsavedChanges(hasChanges) {
				this.hasUnsavedChanges = hasChanges

				// Show appropriate message when changes occur
				if (hasChanges && this.isOffline) {
					this.showLoading("Changes will be saved when you're back online")
				} else if (!hasChanges && !this.isOffline) {
					this.showSuccess('All changes have been saved')
				}
			},
			/**
			 * Show the loading message
			 * @param {string} message - The message to show
			 */
			showLoading(message = 'Loading...') {
				this.clearHideTimeout()
				this.status = 'loading'
				this.message = message
			},
			/**
			 * Show the success message
			 * @param {string} message - The message to show
			 * @param {number} duration - The duration to show the message
			 */
			showSuccess(message = 'Success!', duration = 3000) {
				this.clearHideTimeout()
				this.status = 'success'
				this.message = message
				this.autoHide(duration)
			},
			/**
			 * Show the error message
			 * @param {string} message - The message to show
			 * @param {number} duration - The duration to show the message
			 */
			showError(message = 'An error occurred', duration = 5000) {
				this.clearHideTimeout()
				this.status = 'error'
				this.message = message
				this.autoHide(duration)
			},
			/**
			 * Hide the status banner
			 */
			hide() {
				this.status = ''
				this.message = ''
			},
			/**
			 * Clear the hide timeout
			 */
			clearHideTimeout() {
				if (this.hideTimeout) {
					clearTimeout(this.hideTimeout)
					this.hideTimeout = null
				}
			},
			/**
			 * Auto hide the status banner
			 * @param {number} duration - The duration to show the message
			 */
			autoHide(duration) {
				this.clearHideTimeout()
				this.hideTimeout = setTimeout(() => {
					this.hide()
				}, duration)
			}
		}
	}
</script>

<style scoped>
	.status-banner-wrapper {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		z-index: 1000;
		pointer-events: none;
	}

	.status-banner {
		width: 100%;
		background: #007aff;
		color: #fff;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		pointer-events: auto;
	}

	.status-banner--offline {
		background: #f0ad4e !important; /* Always orange for offline */
		color: #fff;
	}

	.status-banner--success {
		background: #34c263;
		color: #fff;
	}

	.status-banner--error {
		background: #ea6856;
		color: #fff;
	}

	.status-banner__content {
		padding: 12px 16px;
		text-align: center;
	}

	/* Progress bar styles */
	.status-banner__progress {
		height: 2px; /* Slightly thinner */
		width: 100%;
		background: rgba(0, 0, 0, 0.1); /* Darker background for better contrast */
		overflow: hidden;
	}

	.status-banner__progress-bar {
		height: 100%;
		width: 100%;
		transform-origin: left center;
	}

	.status-banner__progress-bar--loading {
		background: rgba(255, 255, 255, 0.9); /* More opaque white */
		animation: loading-rtl 2s infinite;
	}

	.status-banner__progress-bar--success {
		background: #34c263; /* Keep green */
		animation: complete 0.5s forwards;
	}

	.status-banner__progress-bar--error {
		background: #ea6856; /* Keep red */
		animation: complete 0.5s forwards;
	}

	/* Slide animation */
	.slide-enter-active,
	.slide-leave-active {
		transition: transform 0.3s ease;
	}

	.slide-enter-from,
	.slide-leave-to {
		transform: translateY(-100%);
	}

	.slide-enter-to,
	.slide-leave-from {
		transform: translateY(0);
	}

	/* Progress bar animations */
	@keyframes loading-rtl {
		0% {
			transform: translateX(100%);
		}
		50% {
			transform: translateX(0%);
		}
		100% {
			transform: translateX(-100%);
		}
	}

	@keyframes loading {
		0% {
			transform: translateX(-100%);
		}
		50% {
			transform: translateX(0%);
		}
		100% {
			transform: translateX(100%);
		}
	}

	@keyframes complete {
		0% {
			transform: translateX(-100%);
		}
		100% {
			transform: translateX(0);
		}
	}

	.progress-wrapper {
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		z-index: 1000;
		pointer-events: none;
	}

	.status-banner__progress {
		height: 4px;
		width: 100%;
		background: rgba(0, 0, 0, 0.1);
		overflow: hidden;
	}
</style>
